import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { authConfigured, authHeaders, fn, supabase } from './supabase'

export type MemberStatus = 'pending' | 'approved' | 'revoked'
export type MemberRole = 'admin' | 'scorer' | 'player'

export interface Member {
  id: string
  player_id: string | null
  display_name: string | null
  role: MemberRole
  status: MemberStatus
  claimed_at: string
  approved_at: string | null
  /** Do they want the scheduled report emails? Default on (gap 3). */
  email_opt_in: boolean
}

/**
 * Where the signed-in user stands.
 *
 * `unconfigured` means the app is running against the bundled sample data with
 * no backend at all -- a legitimate way to work on the UI, and the auth layer
 * has to tolerate it rather than lock the app.
 */
export type AuthStage =
  | 'loading'
  | 'unconfigured'
  | 'signed-out'
  | 'needs-claim'
  | 'pending-approval'
  | 'revoked'
  | 'unverified'
  | 'ready'

interface AuthState {
  stage: AuthStage
  session: Session | null
  member: Member | null
  email: string | null
  isAdmin: boolean
  /** Why the membership lookup failed, when it did. */
  lookupError: string | null
  /** Re-reads the claim, e.g. after an admin approves it. */
  refreshMember(): Promise<void>
  signOut(): Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>')
  return v
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [member, setMember] = useState<Member | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [stage, setStage] = useState<AuthStage>(authConfigured() ? 'loading' : 'unconfigured')

  const loadMember = useCallback(async (active: Session | null) => {
    if (!active) {
      setMember(null)
      setStage('signed-out')
      return
    }
    const url = fn('claim')
    if (!url) {
      setStage('unconfigured')
      return
    }
    try {
      const res = await fetch(url, { headers: await authHeaders() })
      if (!res.ok) {
        const detail = await res.text().catch(() => '')
        throw new Error(`claim lookup failed (${res.status}) ${detail.slice(0, 200)}`)
      }
      const body = (await res.json()) as { member: Member | null }
      setLookupError(null)
      setMember(body.member)
      setStage(
        !body.member
          ? 'needs-claim'
          : body.member.status === 'approved'
            ? 'ready'
            : body.member.status === 'revoked'
              ? 'revoked'
              : 'pending-approval',
      )
    } catch (err) {
      /*
       * The lookup failed. This must fail CLOSED.
       *
       * A previous version treated an unknown member as good enough so an
       * offline scorer would not be thrown out mid-game -- but it also let
       * anyone with a session straight into the app whenever the lookup broke,
       * which is how an unapproved account reached the main screen.
       *
       * Offline tolerance now applies only where it was actually earned: to
       * someone already known to be approved. Anyone else is told we could not
       * check, which is the truth.
       */
      setLookupError(err instanceof Error ? err.message : String(err))
      setStage(member?.status === 'approved' ? 'ready' : 'unverified')
    }
  }, [member])

  useEffect(() => {
    if (!supabase) return
    let cancelled = false

    // Sign-in is by emailed code (03.5): the session is established in-page by
    // verifyOtp and nothing ever arrives in the URL, so there is no callback to
    // parse and no payload to scrub out of the address bar.
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setSession(data.session)
      void loadMember(data.session)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      void loadMember(next)
    })

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
    // loadMember is intentionally not a dependency: it closes over `member` and
    // re-subscribing on every membership change would tear down the listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value: AuthState = {
    stage,
    session,
    member,
    email: session?.user.email ?? null,
    isAdmin: member?.status === 'approved' && member.role === 'admin',
    lookupError,
    refreshMember: async () => {
      await loadMember(session)
    },
    signOut: async () => {
      await supabase?.auth.signOut()
      setMember(null)
      setStage('signed-out')
    },
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
